import { useState, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  RecaptchaVerifier,
  PhoneAuthProvider,
  linkWithCredential,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, runTransaction } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../services/firebase';

const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

/**
 * Hook for email/password authentication with phone verification
 */
export function useAuth() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verificationId, setVerificationId] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  /**
   * Register a new account with email + password.
   * Does NOT create the Firestore profile yet — that happens after phone verification.
   * Returns the Firebase user on success.
   *
   * If the email is already registered but has no Firestore profile (orphaned account
   * from a previous failed registration), it tries to sign in instead.
   */
  async function register(email, password) {
    setLoading(true);
    setError(null);

    try {
      // If already signed in with this email (from a previous partial registration),
      // just return the current user
      if (auth.currentUser?.email?.toLowerCase() === email.toLowerCase()) {
        setLoading(false);
        return auth.currentUser;
      }

      const credential = await createUserWithEmailAndPassword(auth, email, password);
      setLoading(false);
      return credential.user;
    } catch (err) {
      // If email is already in use, try signing in (handles orphaned accounts
      // from previous failed registration attempts)
      if (err.code === 'auth/email-already-in-use') {
        try {
          const credential = await signInWithEmailAndPassword(auth, email, password);
          // Check if this account already has a Firestore profile
          const userDoc = await getDoc(doc(db, 'users', credential.user.uid));
          if (userDoc.exists()) {
            // Account is fully set up — they should use login instead
            setError('This email is already registered. Try logging in instead.');
            setLoading(false);
            return null;
          }
          // Orphaned account (no profile) — let them continue registration
          setLoading(false);
          return credential.user;
        } catch (signInErr) {
          console.error('Sign-in fallback error:', signInErr.code, signInErr.message);
          setError('This email is already registered. Try logging in instead.');
          setLoading(false);
          return null;
        }
      }

      console.error('Registration error:', err.code, err.message);
      setError(getAuthErrorMessage(err.code, err.message));
      setLoading(false);
      return null;
    }
  }

  /**
   * Send SMS verification code to the user's phone number.
   * Uses an invisible reCAPTCHA verifier attached to the given button element.
   *
   * On localhost: Skips SMS entirely and returns true immediately (reCAPTCHA
   * doesn't work reliably on localhost). The phone number is still saved to
   * the profile but verification is bypassed for development.
   */
  async function sendPhoneVerification(phoneNumber, buttonId) {
    // On localhost, skip phone verification entirely
    // reCAPTCHA Enterprise doesn't work on localhost even with the testing flag
    if (isLocalhost) {
      console.log('[DEV] Skipping phone verification on localhost for:', phoneNumber);
      setVerificationId('localhost-bypass');
      return true;
    }

    setLoading(true);
    setError(null);

    try {
      // Set up invisible reCAPTCHA if not already initialized
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, buttonId, {
          size: 'invisible',
          callback: () => {},
          'expired-callback': () => {
            setError('reCAPTCHA expired. Please try again.');
          },
        });
      }

      const provider = new PhoneAuthProvider(auth);
      const verId = await provider.verifyPhoneNumber(
        phoneNumber,
        window.recaptchaVerifier
      );
      setVerificationId(verId);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Error sending SMS:', err.code, err.message);
      setError(getAuthErrorMessage(err.code, err.message));
      setLoading(false);
      // Reset reCAPTCHA on error
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (_) {}
        window.recaptchaVerifier = null;
      }
      return false;
    }
  }

  /**
   * Verify the SMS code and link the phone number to the current account.
   * Then create the Firestore user profile + username doc in a transaction.
   *
   * On localhost: Skips actual SMS verification (no code needed), just creates
   * the Firestore profile directly.
   *
   * @param {string} code - 6-digit SMS code (ignored on localhost)
   * @param {object} profileData - { firstName, lastName, displayName, email, username, phone }
   */
  async function verifyPhoneAndCreateProfile(code, profileData) {
    if (!isLocalhost && !verificationId) {
      setError('No verification in progress. Please request a new code.');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const user = auth.currentUser;

      if (!user) {
        throw new Error('No authenticated user. Please try registering again.');
      }

      // On production, link the phone credential to the email/password account
      if (!isLocalhost && verificationId) {
        const phoneCredential = PhoneAuthProvider.credential(verificationId, code);
        await linkWithCredential(user, phoneCredential);
      } else {
        console.log('[DEV] Skipping phone credential linking on localhost');
      }

      // Create Firestore profile + claim username in a transaction
      const usernameLower = profileData.username.toLowerCase();

      await runTransaction(db, async (transaction) => {
        // Check username availability
        const usernameRef = doc(db, 'usernames', usernameLower);
        const usernameSnap = await transaction.get(usernameRef);

        if (usernameSnap.exists()) {
          throw new Error('Username is already taken. Please choose another.');
        }

        // Claim the username
        transaction.set(usernameRef, {
          uid: user.uid,
          email: profileData.email.toLowerCase(),
          createdAt: serverTimestamp(),
        });

        // Create the user profile
        const userRef = doc(db, 'users', user.uid);
        transaction.set(userRef, {
          uid: user.uid,
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          displayName: profileData.displayName,
          email: profileData.email,
          emailLower: profileData.email.toLowerCase(),
          username: profileData.username,
          usernameLower: usernameLower,
          phone: profileData.phone,
          phoneVerified: !isLocalhost, // true in production, false on localhost
          currentStreak: 0,
          longestStreak: 0,
          totalPicks: 0,
          totalCorrect: 0,
          badges: ['newbie'],
          activeBadge: 'newbie',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });

      setLoading(false);
      return true;
    } catch (err) {
      console.error('Phone verification error:', err.code || '', err.message);

      // Handle specific errors
      if (err.message?.includes('already taken')) {
        setError(err.message);
      } else {
        setError(getAuthErrorMessage(err.code, err.message));
      }

      setLoading(false);
      return false;
    }
  }

  /**
   * Login with email or username + password.
   * If identifier contains '@', treat as email. Otherwise, look up email by username.
   */
  async function login(identifier, password) {
    setLoading(true);
    setError(null);

    try {
      let email = identifier;

      // If no '@', treat as username — look up the email
      if (!identifier.includes('@')) {
        const lookupFn = httpsCallable(functions, 'lookupEmailByUsername');
        const result = await lookupFn({ username: identifier.toLowerCase() });
        if (!result.data?.email) {
          setError('Username not found.');
          setLoading(false);
          return null;
        }
        email = result.data.email;
      }

      const credential = await signInWithEmailAndPassword(auth, email, password);
      setLoading(false);
      return credential.user;
    } catch (err) {
      console.error('Login error:', err.code, err.message);

      // If it's a functions error (username not found), it comes as a different format
      if (err.code === 'functions/not-found') {
        setError('Username not found.');
      } else {
        setError(getAuthErrorMessage(err.code, err.message));
      }

      setLoading(false);
      return null;
    }
  }

  /**
   * Send a password reset email
   */
  async function resetPassword(email) {
    setLoading(true);
    setError(null);

    try {
      await sendPasswordResetEmail(auth, email);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Password reset error:', err.code, err.message);
      setError(getAuthErrorMessage(err.code, err.message));
      setLoading(false);
      return false;
    }
  }

  /**
   * Sign out the current user
   */
  async function logout() {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Error signing out:', err);
    }
  }

  /**
   * Check if a username is available (for real-time feedback during registration)
   */
  async function checkUsernameAvailable(username) {
    if (!username || username.length < 3) return null;
    const usernameLower = username.toLowerCase();
    const usernameRef = doc(db, 'usernames', usernameLower);
    const snap = await getDoc(usernameRef);
    return !snap.exists();
  }

  return {
    register,
    sendPhoneVerification,
    verifyPhoneAndCreateProfile,
    login,
    resetPassword,
    logout,
    checkUsernameAvailable,
    loading,
    error,
    clearError,
    codeSent: !!verificationId,
  };
}

/**
 * Map Firebase Auth error codes to user-friendly messages
 */
function getAuthErrorMessage(code, rawMessage) {
  const messages = {
    // Email/Password errors
    'auth/email-already-in-use': 'This email is already registered. Try logging in instead.',
    'auth/invalid-email': 'Invalid email address.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/user-disabled': 'This account has been disabled.',

    // Phone/SMS errors
    'auth/invalid-phone-number': 'Invalid phone number. Please include country code (e.g., +1).',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/invalid-verification-code': 'Invalid verification code. Please try again.',
    'auth/code-expired': 'Verification code expired. Please request a new one.',
    'auth/quota-exceeded': 'SMS quota exceeded. Please try again later.',
    'auth/credential-already-in-use': 'This phone number is already linked to another account.',

    // General errors
    'auth/captcha-check-failed': 'reCAPTCHA verification failed. Please try again.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled.',
    'auth/internal-error': 'Internal error. Please try again.',
    'auth/invalid-app-credential': 'App verification failed. Please refresh and try again.',
    'auth/missing-client-identifier': 'App verification failed. Please refresh and try again.',
  };

  return messages[code] || rawMessage || 'An unexpected error occurred. Please try again.';
}
